import React, { useState, useEffect } from 'react';
import { useAuth } from '../AuthContext';

const LearningPathsTab = () => {
  const { user } = useAuth();
  const [learningPaths, setLearningPaths] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYearLevel, setSelectedYearLevel] = useState(4);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedPathId, setSelectedPathId] = useState(null);
  const [generatingQuestions, setGeneratingQuestions] = useState({});
  const [generationResults, setGenerationResults] = useState({});
  const [expandedPaths, setExpandedPaths] = useState({});
  const [expandedSubtopics, setExpandedSubtopics] = useState({});
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const yearLevels = [3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

  useEffect(() => {
    fetchLearningPaths();
  }, []);

  const fetchLearningPaths = async () => {
    try {
      setLoading(true);
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/admin/learning-paths?user_id=${user.id}`);
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setLearningPaths(data.data || []);
        }
      } else {
        console.error('Failed to fetch learning paths');
      }
    } catch (error) {
      console.error('Error fetching learning paths:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePath = async () => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/admin/learning-paths?user_id=${user.id}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          year_level: selectedYearLevel,
          curriculum_version: '9.0',
          curriculum_name: 'Australian Curriculum Mathematics'
        }),
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setShowCreateModal(false);
          setSelectedPathId(data.data.id);
          setShowUploadModal(true);
          await fetchLearningPaths();
        }
      } else {
        const errorData = await response.json();
        alert(`Failed to create learning path: ${errorData.detail}`);
      }
    } catch (error) {
      console.error('Error creating learning path:', error);
      alert('Error creating learning path');
    }
  };

  const handlePublishPath = async (pathId) => {
    if (!confirm('Are you sure you want to publish this learning path? This will make it the active version for this year level.')) {
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/admin/learning-paths/${pathId}/publish?user_id=${user.id}`, {
        method: 'PUT',
      });
      
      if (response.ok) {
        alert('Learning path published successfully!');
        await fetchLearningPaths();
      } else {
        const errorData = await response.json();
        alert(`Failed to publish learning path: ${errorData.detail}`);
      }
    } catch (error) {
      console.error('Error publishing learning path:', error);
      alert('Error publishing learning path');
    }
  };

  const handleDeletePath = async (pathId) => {
    if (!confirm('Are you sure you want to delete this learning path? This action cannot be undone.')) {
      return;
    }

    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/admin/learning-paths/${pathId}?user_id=${user.id}`, {
        method: 'DELETE',
      });
      
      if (response.ok) {
        alert('Learning path deleted successfully!');
        await fetchLearningPaths();
      } else {
        const errorData = await response.json();
        alert(`Failed to delete learning path: ${errorData.detail}`);
      }
    } catch (error) {
      console.error('Error deleting learning path:', error);
      alert('Error deleting learning path');
    }
  };

  const handleGenerateAllQuestions = async (pathId, yearLevel) => {
    if (!confirm(`Are you sure you want to generate questions for all subtopics and difficulty levels in Year ${yearLevel}? This may take several minutes.`)) {
      return;
    }

    try {
      setGeneratingQuestions(prev => ({ ...prev, [pathId]: true }));
      setGenerationResults(prev => ({ ...prev, [pathId]: null }));

      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/admin/learning-paths/${pathId}/generate-all-questions?user_id=${user.id}`, {
        method: 'POST',
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setGenerationResults(prev => ({ ...prev, [pathId]: data.data }));
          alert(`✅ Bulk question generation completed!\n\n` +
                `📊 Results:\n` +
                `• Total tasks: ${data.data.total_tasks}\n` +
                `• Completed: ${data.data.completed}\n` +
                `• Failed: ${data.data.failed}\n` +
                `• Skipped: ${data.data.skipped}\n` +
                `• Success rate: ${data.data.success_rate}`);
          await fetchLearningPaths();
        } else {
          alert(`Failed to generate questions: ${data.message}`);
        }
      } else {
        const errorData = await response.json();
        alert(`Failed to generate questions: ${errorData.detail}`);
      }
    } catch (error) {
      console.error('Error generating questions:', error);
      alert('Error generating questions');
    } finally {
      setGeneratingQuestions(prev => ({ ...prev, [pathId]: false }));
    }
  };

  const handleGenerateTaskQuestions = async (taskId, subtopicName, difficulty, yearLevel, isRegenerate = false) => {
    const action = isRegenerate ? 'Regenerate' : 'Generate';
    if (!confirm(`${action} questions for "${subtopicName}" - ${difficulty} level (Year ${yearLevel})?`)) {
      return;
    }

    try {
      setGeneratingQuestions(prev => ({ ...prev, [taskId]: true }));

      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const url = `${backendUrl}/api/admin/learning-paths/generate-questions/${taskId}?user_id=${user.id}${isRegenerate ? '&force_regenerate=true' : ''}`;
      
      console.log(`🔍 ${action} questions request:`, {
        taskId,
        subtopicName,
        difficulty,
        yearLevel,
        isRegenerate,
        url
      });
      
      const response = await fetch(url, {
        method: 'POST',
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          alert(`✅ Questions ${isRegenerate ? 'regenerated' : 'generated'} successfully!\n\n` +
                `📝 Generated: ${data.data.questions_generated} questions\n` +
                `🔧 Method: ${data.data.generation_method}\n` +
                `📚 Subtopic: ${subtopicName}\n` +
                `📊 Difficulty: ${difficulty}`);
          await fetchLearningPaths();
        } else {
          alert(`Failed to ${action.toLowerCase()} questions: ${data.message}`);
        }
      } else {
        const errorData = await response.json();
        alert(`Failed to ${action.toLowerCase()} questions: ${errorData.detail}`);
      }
    } catch (error) {
      console.error(`Error ${action.toLowerCase()}ing task questions:`, error);
      alert(`Error ${action.toLowerCase()}ing questions`);
    } finally {
      setGeneratingQuestions(prev => ({ ...prev, [taskId]: false }));
    }
  };

  const handleResetTask = async (taskId, subtopicName, difficulty, yearLevel) => {
    if (!confirm(`Reset the stuck task for "${subtopicName}" - ${difficulty} level (Year ${yearLevel})?\n\nThis will reset the status from "generating" to "pending".`)) {
      return;
    }

    try {
      setGeneratingQuestions(prev => ({ ...prev, [taskId]: true }));

      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/admin/learning-paths/reset-task/${taskId}?user_id=${user.id}`, {
        method: 'POST',
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          alert(`✅ Task reset successfully!\n\nThe task is now available for generation.`);
          await fetchLearningPaths();
        } else {
          alert(`Failed to reset task: ${data.message}`);
        }
      } else {
        const errorData = await response.json();
        alert(`Failed to reset task: ${errorData.detail}`);
      }
    } catch (error) {
      console.error('Error resetting task:', error);
      alert('Error resetting task');
    } finally {
      setGeneratingQuestions(prev => ({ ...prev, [taskId]: false }));
    }
  };

  const togglePathExpansion = (pathId) => {
    setExpandedPaths(prev => ({
      ...prev,
      [pathId]: !prev[pathId]
    }));
  };

  const toggleSubtopicExpansion = (subtopicId) => {
    setExpandedSubtopics(prev => ({
      ...prev,
      [subtopicId]: !prev[subtopicId]
    }));
  };

  const handlePreviewLearningPath = async (pathId, yearLevel) => {
    try {
      setLoadingPreview(true);
      
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/admin/learning-paths/${pathId}/preview?user_id=${user.id}`);
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setPreviewData(data.data);
          setShowPreviewModal(true);
        } else {
          alert(`Failed to load preview: ${data.message}`);
        }
      } else {
        const errorData = await response.json();
        alert(`Failed to load preview: ${errorData.detail}`);
      }
    } catch (error) {
      console.error('Error loading preview:', error);
      alert('Error loading preview');
    } finally {
      setLoadingPreview(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading learning paths...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Learning Paths Management</h2>
          <p className="text-gray-600">Create and manage AI-generated learning paths for different year levels</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
        >
          ➕ Create New Learning Path
        </button>
      </div>

      {/* Learning Paths Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">Existing Learning Paths</h3>
        </div>
        
        {learningPaths.length === 0 ? (
          <div className="px-6 py-12 text-center">
            <div className="text-gray-400 text-6xl mb-4">📚</div>
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Learning Paths Yet</h3>
            <p className="text-gray-500 mb-4">Create your first learning path to get started</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
            >
              Create Learning Path
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <div className="space-y-4">
              {learningPaths.map((path) => (
                <div key={path.id} className="border border-gray-200 rounded-lg">
                  {/* Learning Path Header */}
                  <div className="bg-gray-50 px-6 py-4 border-b border-gray-200">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-4">
                        <button
                          onClick={() => togglePathExpansion(path.id)}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          {expandedPaths[path.id] ? '🔽' : '▶️'}
                        </button>
                        <div>
                          <h4 className="text-lg font-semibold text-gray-900">
                            Year {path.year_level} Mathematics - v{path.version}
                          </h4>
                          <div className="flex items-center space-x-4 mt-1">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              path.status === 'published' 
                                ? 'bg-green-100 text-green-800' 
                                : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {path.status === 'published' ? '✅ Published' : '📝 Draft'}
                            </span>
                            <span className="text-sm text-gray-500">
                              {path.strands?.length || 0} strands • Created {new Date(path.created_at).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2">
                        <button
                          onClick={() => {
                            setSelectedPathId(path.id);
                            setShowUploadModal(true);
                          }}
                          className="text-blue-600 hover:text-blue-900 px-3 py-1 text-sm"
                        >
                          📝 Edit
                        </button>
                        
                        <button
                          onClick={() => handleGenerateAllQuestions(path.id, path.year_level)}
                          disabled={generatingQuestions[path.id]}
                          className={`px-3 py-1 text-sm rounded ${
                            generatingQuestions[path.id] 
                              ? 'text-gray-400 cursor-not-allowed' 
                              : 'text-purple-600 hover:text-purple-900 hover:bg-purple-50'
                          }`}
                          title="Generate questions for all subtopics and difficulty levels"
                        >
                          {generatingQuestions[path.id] ? '🔄 Generating All...' : '🤖 Bulk Generate'}
                        </button>
                        
                        <button
                          onClick={() => handlePreviewLearningPath(path.id, path.year_level)}
                          disabled={loadingPreview}
                          className={`px-3 py-1 text-sm rounded ${
                            loadingPreview
                              ? 'text-gray-400 cursor-not-allowed' 
                              : 'text-indigo-600 hover:text-indigo-900 hover:bg-indigo-50'
                          }`}
                          title="Preview all questions in this learning path"
                        >
                          {loadingPreview ? '🔄 Loading...' : '👁️ Preview Questions'}
                        </button>
                        
                        {path.status === 'draft' && (
                          <>
                            <button
                              onClick={() => handlePublishPath(path.id)}
                              className="text-green-600 hover:text-green-900 px-3 py-1 text-sm"
                            >
                              🚀 Publish
                            </button>
                            <button
                              onClick={() => handleDeletePath(path.id)}
                              className="text-red-600 hover:text-red-900 px-3 py-1 text-sm"
                            >
                              🗑️ Delete
                            </button>
                          </>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Expandable Content - Strands and Subtopics */}
                  {expandedPaths[path.id] && (
                    <div className="px-6 py-4">
                      {!path.strands || path.strands.length === 0 ? (
                        <div className="text-center py-8 text-gray-500">
                          <div className="text-4xl mb-2">📋</div>
                          <p>No curriculum structure uploaded yet</p>
                          <button
                            onClick={() => {
                              setSelectedPathId(path.id);
                              setShowUploadModal(true);
                            }}
                            className="mt-2 text-blue-600 hover:text-blue-900 text-sm"
                          >
                            Upload Curriculum Structure
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {path.strands.map((strand) => (
                            <div key={strand.id} className="border-l-4 border-blue-200 pl-4">
                              <h5 className="font-medium text-gray-900 mb-2">
                                {strand.strand_name} ({strand.strand_code})
                              </h5>
                              
                              {strand.subtopics && strand.subtopics.length > 0 ? (
                                <div className="space-y-2">
                                  {strand.subtopics.map((subtopic) => (
                                    <div key={subtopic.id} className="ml-4">
                                      <div className="flex items-center justify-between py-2">
                                        <button
                                          onClick={() => toggleSubtopicExpansion(subtopic.id)}
                                          className="flex items-center space-x-2 text-gray-700 hover:text-gray-900"
                                        >
                                          <span>{expandedSubtopics[subtopic.id] ? '🔽' : '▶️'}</span>
                                          <span className="font-medium">{subtopic.subtopic_name}</span>
                                          <span className="text-xs text-gray-500">({subtopic.subtopic_code})</span>
                                        </button>
                                      </div>
                                      
                                      {/* Expandable Difficulty Levels */}
                                      {expandedSubtopics[subtopic.id] && subtopic.tasks && (
                                        <div className="ml-6 space-y-2 border-l-2 border-gray-200 pl-4">
                                          {subtopic.tasks.map((task) => (
                                            <div key={task.id} className="flex items-center justify-between py-2 bg-gray-50 px-3 rounded">
                                              <div className="flex items-center space-x-3">
                                                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded ${
                                                  task.difficulty === 'easy' ? 'bg-green-100 text-green-800' :
                                                  task.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                                  'bg-red-100 text-red-800'
                                                }`}>
                                                  {task.difficulty.toUpperCase()}
                                                </span>
                                                <span className="text-sm text-gray-600">
                                                  Target: {task.task_count} questions
                                                </span>
                                                <span className={`text-xs px-2 py-1 rounded ${
                                                  task.generation_status === 'completed' ? 'bg-green-100 text-green-700' :
                                                  task.generation_status === 'generating' ? 'bg-blue-100 text-blue-700' :
                                                  task.generation_status === 'failed' ? 'bg-red-100 text-red-700' :
                                                  'bg-gray-100 text-gray-700'
                                                }`}>
                                                  {task.generation_status === 'completed' && `✅ ${task.questions_count} generated`}
                                                  {task.generation_status === 'generating' && '🔄 Generating...'}
                                                  {task.generation_status === 'failed' && '❌ Failed'}
                                                  {task.generation_status === 'pending' && '⏳ Pending'}
                                                </span>
                                              </div>
                                              
                                              <div className="flex space-x-2">
                                                {/* Reset button for stuck tasks */}
                                                {task.generation_status === 'generating' && !generatingQuestions[task.id] && (
                                                  <button
                                                    onClick={() => handleResetTask(
                                                      task.id, 
                                                      subtopic.subtopic_name, 
                                                      task.difficulty, 
                                                      path.year_level
                                                    )}
                                                    className="text-xs px-2 py-1 rounded text-red-600 hover:text-red-900 hover:bg-red-50"
                                                    title="Reset this stuck task"
                                                  >
                                                    🔧 Reset
                                                  </button>
                                                )}
                                                
                                                {/* Main generate/regenerate button */}
                                                <button
                                                  onClick={() => handleGenerateTaskQuestions(
                                                    task.id, 
                                                    subtopic.subtopic_name, 
                                                    task.difficulty, 
                                                    path.year_level,
                                                    task.generation_status === 'completed' || task.generation_status === 'failed'  // isRegenerate = true if completed OR failed
                                                  )}
                                                  disabled={generatingQuestions[task.id] || task.generation_status === 'generating'}
                                                  className={`text-xs px-3 py-1 rounded ${
                                                    generatingQuestions[task.id] || task.generation_status === 'generating'
                                                      ? 'text-gray-400 cursor-not-allowed' 
                                                      : task.generation_status === 'completed'
                                                      ? 'text-orange-600 hover:text-orange-900 hover:bg-orange-50'
                                                      : task.generation_status === 'failed'
                                                      ? 'text-red-600 hover:text-red-900 hover:bg-red-50'
                                                      : 'text-purple-600 hover:text-purple-900 hover:bg-purple-50'
                                                  }`}
                                                >
                                                  {generatingQuestions[task.id] ? '🔄 Generating...' :
                                                   task.generation_status === 'completed' ? '🔄 Regenerate' :
                                                   task.generation_status === 'failed' ? '🔄 Retry (Force)' :
                                                   '🤖 Generate'}
                                                </button>
                                              </div>
                                            </div>
                                          ))}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              ) : (
                                <p className="text-sm text-gray-500 ml-4">No subtopics available</p>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Create Learning Path Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-4">Create New Learning Path</h3>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Year Level
                </label>
                <select
                  value={selectedYearLevel}
                  onChange={(e) => setSelectedYearLevel(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  {yearLevels.map(year => (
                    <option key={year} value={year}>Year {year}</option>
                  ))}
                </select>
              </div>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-700">
                  <strong>Note:</strong> After creating the learning path, you'll need to upload the curriculum structure JSON file to define the content.
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowCreateModal(false)}
                className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreatePath}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors"
              >
                Create Learning Path
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Curriculum Modal */}
      {showUploadModal && selectedPathId && (
        <CurriculumUploadModal
          pathId={selectedPathId}
          onClose={() => {
            setShowUploadModal(false);
            setSelectedPathId(null);
          }}
          onSuccess={() => {
            setShowUploadModal(false);
            setSelectedPathId(null);
            fetchLearningPaths();
          }}
        />
      )}

      {/* Learning Path Preview Modal */}
      <LearningPathPreviewModal
        isOpen={showPreviewModal}
        onClose={() => {
          setShowPreviewModal(false);
          setPreviewData(null);
        }}
        previewData={previewData}
      />
    </div>
  );
};

// Curriculum Upload Modal Component
const CurriculumUploadModal = ({ pathId, onClose, onSuccess }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [jsonData, setJsonData] = useState(null);
  const [jsonError, setJsonError] = useState('');

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile && selectedFile.type === 'application/json') {
      setFile(selectedFile);
      setJsonError('');
      // Read and validate JSON
      const reader = new FileReader();
      reader.onload = (event) => {
        try {
          const json = JSON.parse(event.target.result);
          setJsonData(json);
        } catch (error) {
          setJsonError('Invalid JSON file format');
          setFile(null);
        }
      };
      reader.readAsText(selectedFile);
    } else {
      setJsonError('Please select a valid JSON file');
      setFile(null);
    }
  };

  const handleUpload = async () => {
    if (!jsonData) return;

    setUploading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/admin/learning-paths/${pathId}/upload-curriculum`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(jsonData),
      });
      
      if (response.ok) {
        alert('Curriculum structure uploaded successfully!');
        onSuccess();
      } else {
        const errorData = await response.json();
        alert(`Failed to upload curriculum: ${errorData.detail}`);
      }
    } catch (error) {
      console.error('Error uploading curriculum:', error);
      alert('Error uploading curriculum structure');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-xl max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <h3 className="text-xl font-bold text-gray-900 mb-4">Upload Curriculum Structure</h3>
        
        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Curriculum JSON File
            </label>
            <input
              type="file"
              accept=".json"
              onChange={handleFileChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {jsonError && (
              <p className="text-red-600 text-sm mt-1">{jsonError}</p>
            )}
          </div>

          {jsonData && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="font-medium text-green-800 mb-2">✅ Valid JSON Detected</h4>
              <div className="text-sm text-green-700 space-y-1">
                <p><strong>Year:</strong> {jsonData.year}</p>
                <p><strong>Curriculum:</strong> {jsonData.curriculum_name}</p>
                <p><strong>Version:</strong> {jsonData.curriculum_version}</p>
                <p><strong>Content Strands:</strong> {jsonData.content_strands?.length || 0}</p>
                <p><strong>Total Subtopics:</strong> {
                  jsonData.content_strands?.reduce((total, strand) => 
                    total + (strand.subtopics?.length || 0), 0
                  ) || 0
                }</p>
              </div>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h4 className="font-medium text-blue-800 mb-2">📋 Required JSON Structure</h4>
            <p className="text-sm text-blue-700">
              The JSON file should contain the curriculum structure with year, content_strands, subtopics, and task definitions as specified in the documentation.
            </p>
          </div>
        </div>

        <div className="flex justify-end space-x-3 mt-6">
          <button
            onClick={onClose}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-lg font-medium transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleUpload}
            disabled={!jsonData || uploading}
            className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {uploading ? '⏳ Uploading...' : '📤 Upload Structure'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Learning Path Preview Modal Component
const LearningPathPreviewModal = ({ isOpen, onClose, previewData }) => {
  const [expandedStrands, setExpandedStrands] = useState({});
  const [expandedSubtopics, setExpandedSubtopics] = useState({});
  const [expandedTasks, setExpandedTasks] = useState({});

  const toggleStrandExpansion = (strandId) => {
    setExpandedStrands(prev => ({
      ...prev,
      [strandId]: !prev[strandId]
    }));
  };

  const toggleSubtopicExpansion = (subtopicId) => {
    setExpandedSubtopics(prev => ({
      ...prev,
      [subtopicId]: !prev[subtopicId]
    }));
  };

  const toggleTaskExpansion = (taskId) => {
    setExpandedTasks(prev => ({
      ...prev,
      [taskId]: !prev[taskId]
    }));
  };

  if (!isOpen || !previewData) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl shadow-xl max-w-6xl w-full h-[90vh] flex flex-col">
        {/* Header - Fixed */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-4 rounded-t-2xl flex-shrink-0">
          <div className="flex justify-between items-center">
            <div>
              <h3 className="text-2xl font-bold">
                Year {previewData.year_level} Mathematics Learning Path Preview
              </h3>
              <p className="text-indigo-100 mt-1">
                Version {previewData.version} • {previewData.status} • {previewData.curriculum_name}
              </p>
            </div>
            <button
              onClick={onClose}
              className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors"
            >
              ✕
            </button>
          </div>
        </div>

        {/* Statistics Panel - Fixed */}
        <div className="bg-gray-50 px-6 py-4 border-b flex-shrink-0">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-900">{previewData.statistics.total_questions}</div>
              <div className="text-sm text-gray-600">Total Questions</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{previewData.statistics.questions_by_difficulty.easy}</div>
              <div className="text-sm text-gray-600">Easy</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">{previewData.statistics.questions_by_difficulty.medium}</div>
              <div className="text-sm text-gray-600">Medium</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">{previewData.statistics.questions_by_difficulty.hard}</div>
              <div className="text-sm text-gray-600">Hard</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{previewData.statistics.strands_count}</div>
              <div className="text-sm text-gray-600">Content Strands</div>
            </div>
          </div>
        </div>

        {/* Content - Scrollable */}
        <div className="flex-1 overflow-y-auto">
          <div className="p-6">
            {previewData.strands.length === 0 ? (
              <div className="text-center py-12">
                <div className="text-6xl mb-4">📚</div>
                <h3 className="text-xl font-medium text-gray-900 mb-2">No Content Available</h3>
                <p className="text-gray-600">This learning path doesn't have any content strands yet.</p>
              </div>
            ) : (
              <div className="space-y-6">
                {previewData.strands.map((strand) => (
                <div key={strand.id} className="border border-gray-200 rounded-lg overflow-hidden">
                  {/* Strand Header */}
                  <div className="bg-blue-50 border-b border-blue-200">
                    <button
                      onClick={() => toggleStrandExpansion(strand.id)}
                      className="w-full px-6 py-4 flex items-center justify-between hover:bg-blue-100 transition-colors"
                    >
                      <div className="flex items-center space-x-3">
                        <span className="text-blue-600">
                          {expandedStrands[strand.id] ? '🔽' : '▶️'}
                        </span>
                        <div className="text-left">
                          <h4 className="text-lg font-semibold text-blue-900">
                            {strand.strand_name} ({strand.strand_code})
                          </h4>
                          <p className="text-sm text-blue-700">
                            {strand.subtopics.length} subtopics
                          </p>
                        </div>
                      </div>
                    </button>
                  </div>

                  {/* Strand Content */}
                  {expandedStrands[strand.id] && (
                    <div className="p-6 space-y-4">
                      {strand.subtopics.map((subtopic) => (
                        <div key={subtopic.id} className="border border-gray-100 rounded-lg">
                          {/* Subtopic Header */}
                          <div className="bg-gray-50 border-b border-gray-200">
                            <button
                              onClick={() => toggleSubtopicExpansion(subtopic.id)}
                              className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-100 transition-colors"
                            >
                              <div className="flex items-center space-x-2">
                                <span className="text-gray-600">
                                  {expandedSubtopics[subtopic.id] ? '🔽' : '▶️'}
                                </span>
                                <div className="text-left">
                                  <h5 className="font-medium text-gray-900">
                                    {subtopic.subtopic_name} ({subtopic.subtopic_code})
                                  </h5>
                                  <p className="text-sm text-gray-600">
                                    {subtopic.tasks.length} difficulty levels • {subtopic.tasks.reduce((sum, task) => sum + task.questions.length, 0)} total questions
                                  </p>
                                </div>
                              </div>
                            </button>
                          </div>

                          {/* Subtopic Content */}
                          {expandedSubtopics[subtopic.id] && (
                            <div className="p-4 space-y-3">
                              {subtopic.tasks.map((task) => (
                                <div key={task.id} className="border border-gray-100 rounded">
                                  {/* Task Header */}
                                  <div className="bg-white border-b border-gray-100 p-3">
                                    <button
                                      onClick={() => toggleTaskExpansion(task.id)}
                                      className="w-full flex items-center justify-between"
                                    >
                                      <div className="flex items-center space-x-2">
                                        <span className="text-gray-500">
                                          {expandedTasks[task.id] ? '🔽' : '▶️'}
                                        </span>
                                        <span className={`px-2 py-1 text-xs font-semibold rounded ${
                                          task.difficulty === 'easy' ? 'bg-green-100 text-green-800' :
                                          task.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                                          'bg-red-100 text-red-800'
                                        }`}>
                                          {task.difficulty.toUpperCase()}
                                        </span>
                                        <span className="text-sm font-medium text-gray-900">
                                          {task.questions.length} Questions Generated
                                        </span>
                                      </div>
                                    </button>
                                  </div>

                                  {/* Questions */}
                                  {expandedTasks[task.id] && (
                                    <div className="p-4 space-y-4">
                                      {task.questions.length === 0 ? (
                                        <p className="text-center text-gray-500 py-4">No questions generated yet</p>
                                      ) : (
                                        task.questions.map((question, idx) => (
                                          <div key={question.id} className="border border-gray-200 rounded-lg p-4 bg-white">
                                            <div className="mb-3">
                                              <span className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded mr-2">
                                                Question {idx + 1}
                                              </span>
                                            </div>

                                            {/* Question Text */}
                                            <div className="mb-4">
                                              <h6 className="font-medium text-gray-900 mb-2">Question:</h6>
                                              <p className="text-gray-800">{question.question_text}</p>
                                            </div>

                                            {/* Options */}
                                            <div className="mb-4">
                                              <h6 className="font-medium text-gray-900 mb-2">Options:</h6>
                                              <div className="space-y-2">
                                                {question.options.map((option, optIdx) => (
                                                  <div key={optIdx} className={`p-2 rounded border ${
                                                    option === question.correct_answer
                                                      ? 'bg-green-50 border-green-200 text-green-800'
                                                      : 'bg-gray-50 border-gray-200'
                                                  }`}>
                                                    <span className="font-medium">
                                                      {String.fromCharCode(65 + optIdx)}.
                                                    </span> {option}
                                                    {option === question.correct_answer && (
                                                      <span className="ml-2 text-green-600 font-medium">✓ Correct</span>
                                                    )}
                                                  </div>
                                                ))}
                                              </div>
                                            </div>

                                            {/* Explanation */}
                                            {question.explanation && (
                                              <div className="mb-3">
                                                <h6 className="font-medium text-gray-900 mb-1">Explanation:</h6>
                                                <p className="text-gray-700 text-sm bg-blue-50 p-3 rounded">
                                                  {question.explanation}
                                                </p>
                                              </div>
                                            )}

                                            {/* Hint */}
                                            {question.hint && (
                                              <div>
                                                <h6 className="font-medium text-gray-900 mb-1">Hint:</h6>
                                                <p className="text-gray-600 text-sm bg-yellow-50 p-2 rounded">
                                                  💡 {question.hint}
                                                </p>
                                              </div>
                                            )}
                                          </div>
                                        ))
                                      )}
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
          </div>
        </div>

        {/* Footer - Fixed */}
        <div className="bg-gray-50 px-6 py-4 border-t flex justify-end flex-shrink-0">
          <button
            onClick={onClose}
            className="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Close Preview
          </button>
        </div>
      </div>
    </div>
  );
};

export default LearningPathsTab;